import { useState } from "react"
import Icon from "@/v2-ui/icon"
import type { TenantUserActivationFragment } from "@/v2-backoffice-client/tenant/migration/__types__/tenant.migration.fragment"
import MigrationProgress from "@/v2-backoffice-client/tenant/migration/MigrationProgress"
import MigrationCenterTeamCard from "@/v2-backoffice-client/tenant/migration/center/MigrationCenterTeamCard"
import MigrationCenterScopeSendButton from "@/v2-backoffice-client/tenant/migration/center/MigrationCenterScopeSendButton"
import type { ActivationRunOutcome } from "@/v2-backoffice-client/tenant/migration/tenantMigration.constants"
import type {
  MigrationCenterMemberSort,
  MigrationCenterMemberSortKey,
  MigrationCenterTenantDescriptor,
} from "@/v2-backoffice-client/tenant/migration/center/tenant.migration.center.types"
import {
  rollupFromRows,
  sendableRows,
} from "@/v2-backoffice-client/tenant/migration/center/tenant.migration.center.utils"

export type MigrationCenterCustomerGroupProps = {
  customerName: string
  tenants: (MigrationCenterTenantDescriptor & {
    rows: TenantUserActivationFragment[]
  })[]
  isLoading: boolean
  /** The lone anonymous group of a single-team selection hides its header. */
  showHeader: boolean
  sort: MigrationCenterMemberSort
  onSort(key: MigrationCenterMemberSortKey): void
  outcomes: Record<number, ActivationRunOutcome>
  onOutcome(userId: number, outcome: ActivationRunOutcome): void
  onExclude(userId: number): void
  onChanged?(): void
}

/**
 * One customer of the Migration Center: a sticky, collapsible header with
 * the customer-wide progress and send button, over its team cards. The
 * send targets exactly the listed members of every team in the group.
 */
function MigrationCenterCustomerGroup(
  props: MigrationCenterCustomerGroupProps,
) {
  const {
    customerName,
    tenants,
    isLoading,
    showHeader,
    sort,
    onSort,
    outcomes,
    onOutcome,
    onExclude,
    onChanged,
  } = props

  const [collapsed, setCollapsed] = useState(false)
  const groupRows = tenants.flatMap((t) => t.rows)
  const sendTeams = tenants.map((t) => ({
    tenantId: t.tenantId,
    userIds: sendableRows(t.rows).map((r) => r.UserId),
  }))

  return (
    <section className="flex flex-col">
      {showHeader && (
        <div className="sticky top-0 z-20 flex h-9 items-center gap-3 bg-background">
          <button
            type="button"
            className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
            data-feature-code="UI_MigrationCenter_CustomerCollapse"
            onClick={() => {
              setCollapsed((prev) => !prev)
            }}
          >
            <Icon
              src={collapsed ? "angle-small-right" : "angle-small-down"}
              size={14}
              className="shrink-0 text-muted-foreground"
            />
            <h3 className="truncate text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {customerName}
            </h3>
          </button>
          <MigrationProgress
            rollup={rollupFromRows(groupRows)}
            className="w-44"
          />
          <MigrationCenterScopeSendButton
            teams={sendTeams}
            scopeLabel={customerName}
            onOutcome={onOutcome}
            onChanged={onChanged}
          />
        </div>
      )}
      {!collapsed && (
        <div className="flex flex-col gap-3">
          {tenants.map((tenant) => (
            <MigrationCenterTeamCard
              key={tenant.tenantId}
              tenant={tenant}
              rows={tenant.rows}
              isLoading={isLoading}
              stickyBelowCustomerHeader={showHeader}
              sort={sort}
              onSort={onSort}
              outcomes={outcomes}
              onOutcome={onOutcome}
              onExclude={onExclude}
              onChanged={onChanged}
            />
          ))}
        </div>
      )}
    </section>
  )
}

export default MigrationCenterCustomerGroup
